import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import { useAuth } from "../../context/AuthContext";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from "../../helpers/Toast";
import { StartAndEndExecutionDTO, StartAndEndExecutionDTOSchema } from "../../models/AssistanceExecution/StartAndEndExecutionDTO";
import { TimeHistoryDTO } from "../../models/TimeHistory/TimeHistoryDTO";
import { AssistanceService } from "../../services/AssistanceService";
import { RenderCountersInputs } from "../Assistances/RenderCountersInputs";
import ButtonComponent from "../ButtonComponent";
import InputComponent from "../InputComponent";
import MultiSelectComponent from "../MultiSelectComponent";
import { SelectItemsProps } from "../Select2Component";

type AssistanceExecutionsComponentType = {
    assistanceId: number;
    reloadExecutionsTable: () => void;
    isComplete: boolean;
    onFetchExecutionsData?: () => void;
    closeModalButtonIsVisible?: boolean;
    close?: () => void;
}

export function ExecutionsInsertComponent({ assistanceId, reloadExecutionsTable, isComplete, ...props }: AssistanceExecutionsComponentType) {
    const assistanceService = new AssistanceService();
    const intl = useIntl();
    const [technicians, setTechnicians] = useState<SelectItemsProps[]>([]);
    const { currentUser, isAdmin, isAdministrative, isTechnician } = useAuth();
    const [selectedTechnicians, setSelectedTechnicians] = useState<string>(
        isTechnician() && currentUser?.id ? currentUser.id : ""
    );
    const today = new Date().toISOString().split('T')[0];
    const [day, setDay] = useState<string>(today);
    const [startTime, setStartTime] = useState<string>("");
    const [endTime, setEndTime] = useState<string>("");
    const [pending, setPending] = useState<string | null>(null);
    const [workDone, setWorkDone] = useState<string | null>(null);
    const [isLoadingNewExecution, setIsLoadingNewExecution] = useState<boolean>(false);

    // Add state variables for modules
    const { availableModules, counterOptions } = useAvailableModulesContext();
    const isActiveSimultaneousTimeRecording = availableModules.includes("SimultaneousTimeRecording");
    const isActiveCountersModule = availableModules.includes("IsActiveCountersModule");
    const isActiveErpSupportModule = availableModules.includes("IsActiveErpSupportModule");
    const isActiveWorkDoneAndPendingInExecutions = availableModules.includes("IsActiveWorkDoneAndPendingInExecutions");
    const [localTimeHistory, setLocalTimeHistory] = useState<TimeHistoryDTO>({ counter1: 0, counter2: 0, counter3: 0 } as TimeHistoryDTO);
    const isMultiSelectDisabled =
        isComplete ||
        isLoadingNewExecution ||
        (!isAdmin() && !isAdministrative() && !isActiveSimultaneousTimeRecording);

    const getTechnicians = async () => {
        const list: SelectItemsProps[] = [];
        setIsLoadingNewExecution(true);
        var response = await assistanceService.GetWorkshopTechnicians();
        setIsLoadingNewExecution(false);

        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        response.obj.forEach((element) => {
            var item: SelectItemsProps = {
                value: element.id,
                label: element.name,
            };
            list.push(item);
        });

        setTechnicians(list);
    };

    const addNewExecution = async () => {
        const executionInfo: StartAndEndExecutionDTO = {
            executionStart: new Date(`${day}T${startTime}`),
            executionEnd: new Date(`${day}T${endTime}`),
            assistanceId: assistanceId,
            technicianId: selectedTechnicians,
            pending: pending,
            workDone: workDone,
        };

        // Add counter values to the execution data when both modules are active
        if (isActiveCountersModule && isActiveErpSupportModule) {
            // Use CountersDTO to pass counter values
            executionInfo.counters = {
                counter1: localTimeHistory.counter1,
                counter2: localTimeHistory.counter2,
                counter3: localTimeHistory.counter3,
                counter4: localTimeHistory.counter4,
                counter5: localTimeHistory.counter5,
                counter6: localTimeHistory.counter6,
                unit1: localTimeHistory.unit1,
                unit2: localTimeHistory.unit2,
                unit3: localTimeHistory.unit3,
                unit4: localTimeHistory.unit4,
                unit5: localTimeHistory.unit5,
                unit6: localTimeHistory.unit6,
            };
        }

        var responseValidate = StartAndEndExecutionDTOSchema.validate(executionInfo, { allowUnknown: true });
        if (responseValidate.error != null) {
            var message = responseValidate.error!.message;
            Toast.Show("error", message);
            return false;
        }

        setIsLoadingNewExecution(true);
        var response = await assistanceService.AddExecutionBackoffice(executionInfo);
        setIsLoadingNewExecution(false);

        if (response.success !== true || response.obj == null) {
            Toast.Show("error", response.message);
            return;
        }

        Toast.Show("success", "Execução inserida com sucesso");

        // Call onFetchExecutionsData to update parent with new data including counters
        if (props.onFetchExecutionsData) {
            props.onFetchExecutionsData();
        }

        reloadExecutionsTable();
        resetInputsFields();
    };

    const resetInputsFields = () => {
        setSelectedTechnicians("");
        setDay("");
        setStartTime("");
        setEndTime("");
        setPending("");
        setWorkDone("");
    }

    useEffect(() => {
        if (assistanceId !== 0) {
            getTechnicians();
        }
    }, [assistanceId])

    return (
        <div>
            <Row style={{ margin: "1rem 0px" }}>
                <MultiSelectComponent
                    xl={8}
                    lg={8}
                    md={8}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    label={"Técnico"}
                    items={technicians}
                    setSelectedString={setSelectedTechnicians}
                    selectString={selectedTechnicians}
                    isDisabled={isMultiSelectDisabled}
                />

                <InputComponent xl={4} lg={4} md={4} sm={6} xs={6}
                    style={{ width: "100%" }}
                    type={"date"}
                    value={day}
                    label={"Dia"}
                    readOnly={isComplete === false ? isLoadingNewExecution : true}
                    showLoadingSkeleton={isLoadingNewExecution}
                    onChange={(t: string) => {
                        setDay(t);
                    }}
                />

                <InputComponent xl={4} lg={4} md={4} sm={4} xs={4}
                    style={{ width: "100%" }}
                    type={"time"}
                    value={startTime}
                    label={"Hora inicio"}
                    readOnly={isComplete === false ? isLoadingNewExecution : true}
                    showLoadingSkeleton={isLoadingNewExecution}
                    onChange={(t: string) => {
                        setStartTime(t);
                    }}
                />

                <InputComponent xl={4} lg={4} md={4} sm={4} xs={4}
                    style={{ width: "100%" }}
                    type={"time"}
                    value={endTime}
                    label={"Hora Fim"}
                    readOnly={isComplete === false ? isLoadingNewExecution : true}
                    showLoadingSkeleton={isLoadingNewExecution}
                    onChange={(t: string) => {
                        setEndTime(t);
                    }}
                />
            </Row>

            {isActiveWorkDoneAndPendingInExecutions &&
                <Row style={{ margin: "1rem 0px" }}>
                    <InputComponent
                        xl={6}
                        lg={6}
                        md={6}
                        sm={12}
                        xs={12}
                        style={{ width: "100%" }}
                        type={"textArea"}
                        value={workDone}
                        label={"Trabalho Efetuado"}
                        readOnly={isComplete}
                        showLoadingSkeleton={false}
                        onChange={(t: string) => {
                            setWorkDone(t);
                        }}
                    />

                    <InputComponent
                        xl={6}
                        lg={6}
                        md={6}
                        sm={12}
                        xs={12}
                        style={{ width: "100%" }}
                        type={"textArea"}
                        value={pending}
                        label={"Trabalho Pendente"}
                        readOnly={isComplete}
                        showLoadingSkeleton={false}
                        onChange={(t: string) => {
                            setPending(t);
                        }}
                    />
                </Row>
            }
            {isActiveCountersModule && isActiveErpSupportModule && isActiveWorkDoneAndPendingInExecutions && (
                <>
                    <label style={{ margin: "0px", padding: "13px", fontWeight: "bold" }}>
                        {intl.formatMessage({ id: "Screens.AssistanceEditB.CountersTitle" })}
                    </label>
                    <Row style={{ margin: "0px", padding: "0px" }}>
                        {RenderCountersInputs(counterOptions, localTimeHistory, setLocalTimeHistory, isComplete)}
                    </Row>
                </>
            )}


            <Row style={{ justifyContent: "flex-end", margin: "1rem 0px" }}>
                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                    <ButtonComponent
                        style={{ width: '100%' }}
                        onClick={addNewExecution}
                        text={intl.formatMessage({ id: "ExecutionsInsertComponent.Insert" })}
                        loading={isLoadingNewExecution}
                        disabled={isComplete === false ? isLoadingNewExecution : true}
                    />
                </Col>
                {props.closeModalButtonIsVisible &&
                    <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                        <ButtonComponent
                            style={{ width: '100%' }}
                            onClick={() => props.close && props.close()}
                            text={intl.formatMessage({ id: "ChangeStatusComponent.Cancel" })}
                            loading={isLoadingNewExecution}
                            disabled={isComplete === false ? isLoadingNewExecution : true}
                        />
                    </Col>
                }
            </Row>
        </div>
    )
}

